import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { Box, Button, IconButton, Tooltip, Typography } from '@material-ui/core'
import {
  Check as CheckIcon,
  CornerDownRight as ReplyIcon,
  Edit2 as EditIcon,
  Trash2 as TrashIcon,
  X as CancelIcon,
} from 'react-feather'

import { ConfirmationDialog } from 'components'
import helpers from 'helpers'
import { CommentReplyForm, CommentText } from './components'

import useStyles from './styles'

const CommentItem = ({
  comment,
  isReply,
  parentId,
  currentUser,
  canDestroyComment,
  onDelete,
  onEdit,
  onAddReply,
  unlinked,
}) => {
  const classes = useStyles()

  const REPLIES_PER_PAGE = 10

  const [editText, setEditText] = useState('')
  const [replyText, setReplyText] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isReplying, setIsReplying] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [repliesOpen, setRepliesOpen] = useState(true)
  const [apiRepliesCount] = useState(comment.replies?.length ?? 0)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [visibleReplies, setVisibleReplies] = useState(REPLIES_PER_PAGE)

  const isAuthor = comment.author?.id === currentUser?.id
  const canEdit = isAuthor
  const canDelete = isAuthor || canDestroyComment
  const showActions = canDelete || canEdit || !isReply

  const handleStartEdit = () => {
    setEditText(comment.comment)
    setIsEditing(true)
  }

  const handleConfirmEdit = async () => {
    const text = editText.trim()
    if (!text) return
    setSubmitting(true)
    try {
      await onEdit(comment.id, text, isReply, parentId)
      setIsEditing(false)
    } finally {
      setSubmitting(false)
    }
  }

  const handleReply = async () => {
    const text = replyText.trim()
    if (!text) return
    setSubmitting(true)
    try {
      await onAddReply(comment.id, text)
      setIsReplying(false)
      setReplyText('')
    } finally {
      setSubmitting(false)
    }
  }

  const handleKeyDown = (e, onEnter) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onEnter()
    }
  }

  return (
    <Box className={isReply ? classes.replyItem : classes.commentItem}>
      <Box className={classes.commentHeader}>
        <Typography className={classes.commentMeta}>
          {isReply && <ReplyIcon size={11} className={classes.replyIcon} />}#
          {String(comment.id).padStart(5, '0')} · {comment.author?.name} ·{' '}
          {helpers.formatters.ptBrFormatDateTime(comment.createdAt)}
          {comment.editedAt && (
            <span className={classes.editedLabel}>
              {' '}
              · editado{' '}
              {helpers.formatters.ptBrFormatDateTime(comment.editedAt)}
            </span>
          )}
        </Typography>

        {showActions && !unlinked && (
          <Box className={classes.actions}>
            {isEditing ? (
              <>
                <Tooltip title="Confirmar">
                  <IconButton
                    size="small"
                    onClick={handleConfirmEdit}
                    disabled={submitting}
                  >
                    <CheckIcon size={14} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancelar">
                  <IconButton size="small" onClick={() => setIsEditing(false)}>
                    <CancelIcon size={14} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                {!isReply && (
                  <Tooltip title="Responder">
                    <IconButton
                      size="small"
                      onClick={() => setIsReplying((prev) => !prev)}
                    >
                      <ReplyIcon size={14} />
                    </IconButton>
                  </Tooltip>
                )}
                {canEdit && (
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={handleStartEdit}>
                      <EditIcon size={14} />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Excluir">
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDeleteOpen(true)}
                    >
                      <TrashIcon size={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        )}
      </Box>

      <CommentText
        text={comment.comment}
        isEditing={isEditing}
        editValue={editText}
        onEditChange={setEditText}
        onKeyDown={(e) => handleKeyDown(e, handleConfirmEdit)}
        disabled={submitting}
      />

      {apiRepliesCount > 0 && (
        <Box display="flex" className={classes.repliesActions}>
          {!repliesOpen && (
            <Button size="small" onClick={() => setRepliesOpen(true)}>
              Exibir respostas ({apiRepliesCount})
            </Button>
          )}
          {repliesOpen && (
            <Button
              size="small"
              className={classes.inheritColor}
              onClick={() => {
                setRepliesOpen(false)
                setVisibleReplies(REPLIES_PER_PAGE)
              }}
            >
              Fechar respostas
            </Button>
          )}
        </Box>
      )}

      {repliesOpen &&
        (comment.replies || []).map((reply, index) => {
          if (index >= visibleReplies && index < apiRepliesCount) return null
          return (
            <CommentItem
              key={reply.id}
              comment={reply}
              isReply
              parentId={comment.id}
              currentUser={currentUser}
              canDestroyComment={canDestroyComment}
              onDelete={onDelete}
              onEdit={onEdit}
              onAddReply={onAddReply}
              unlinked={unlinked}
            />
          )
        })}

      {repliesOpen && apiRepliesCount > visibleReplies && (
        <Box display="flex" className={classes.repliesActions}>
          <Button
            size="small"
            onClick={() => setVisibleReplies((v) => v + REPLIES_PER_PAGE)}
          >
            Exibir mais ({apiRepliesCount - visibleReplies})
          </Button>
        </Box>
      )}

      {isReplying && (
        <CommentReplyForm
          value={replyText}
          onChange={setReplyText}
          onSubmit={handleReply}
          onCancel={() => setIsReplying(false)}
          disabled={submitting}
        />
      )}

      <ConfirmationDialog
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        text="Tem certeza que deseja excluir este comentário? Esta ação não pode ser desfeita."
        textButton="Excluir"
        textButtonCancel="Cancelar"
        actionAcceptButton={() => {
          onDelete(comment.id, isReply, parentId)
          setConfirmDeleteOpen(false)
        }}
      />
    </Box>
  )
}

CommentItem.propTypes = {
  comment: PropTypes.object.isRequired,
  isReply: PropTypes.bool,
  parentId: PropTypes.number,
  currentUser: PropTypes.object,
  canDestroyComment: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAddReply: PropTypes.func.isRequired,
  unlinked: PropTypes.bool,
}

CommentItem.defaultProps = {
  isReply: false,
  parentId: null,
  currentUser: null,
  canDestroyComment: false,
}

export default CommentItem
